/**
 * Curtailment data for property dashboard.
 * Fetches real-time status (get_realtime_curtailment_status) and daily rollups (curtailment_daily) for 7d/30d summary.
 */

import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { CurtailmentStatus, CurtailmentDailyRow } from '@/types/dashboard';

const CURTAILMENT_REALTIME_STALE_MS = 30 * 1000;
const CURTAILMENT_DAILY_STALE_MS = 5 * 60 * 1000;

/** Minimum confidence to show real-time card (Part 3: only when confidence ≥ medium). */
const CONFIDENCE_ORDER: Record<string, number> = {
  very_low: 0,
  low: 1,
  medium: 2,
  high: 3,
  very_high: 4,
};

export function confidenceAtLeastMedium(confidence: string): boolean {
  return (CONFIDENCE_ORDER[confidence] ?? -1) >= CONFIDENCE_ORDER.medium;
}

export interface CurtailmentSummary {
  wasted7dKwh: number;
  wasted30dKwh: number;
  daysWithData: number;
}

export interface UseCurtailmentResult {
  status: CurtailmentStatus | null;
  statusLoading: boolean;
  statusError: string | null;
  dailyRows: CurtailmentDailyRow[];
  dailyLoading: boolean;
  dailyError: string | null;
  summary: CurtailmentSummary;
  refetchStatus: () => void;
  refetchDaily: () => void;
}

function mapRpcRowToStatus(row: Record<string, unknown>): CurtailmentStatus {
  return {
    is_curtailing_now: Boolean(row.is_curtailing_now),
    wasted_power_w: Number(row.wasted_power_w ?? 0),
    wasted_today_kwh: Number(row.wasted_today_kwh ?? 0),
    projected_wasted_by_sunset_kwh: Number(row.projected_wasted_by_sunset_kwh ?? 0),
    curtailment_start: row.curtailment_start != null ? String(row.curtailment_start) : null,
    confidence: (row.confidence as CurtailmentStatus['confidence']) ?? 'very_low',
    method_version: String(row.method_version ?? ''),
    inputs_hash: row.inputs_hash != null ? String(row.inputs_hash) : null,
    computed_at: String(row.computed_at ?? ''),
    property_id: String(row.property_id ?? ''),
    plant_id: row.plant_id != null ? String(row.plant_id) : null,
  };
}

export function useCurtailment(
  propertyId: string | undefined,
  plantId: string | null
): UseCurtailmentResult {
  const thirtyDaysAgo = subDays(new Date(), 30).toISOString().slice(0, 10);

  const {
    data: statusRow,
    isLoading: statusLoading,
    error: statusQueryError,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['curtailment-status', propertyId, plantId],
    queryFn: async (): Promise<CurtailmentStatus | null> => {
      if (!propertyId) return null;
      const { data: rows, error } = await supabase.rpc('get_realtime_curtailment_status', {
        p_property_id: propertyId,
        p_plant_id: plantId ?? undefined,
      });
      if (error) throw error;
      if (!Array.isArray(rows) || rows.length === 0) return null;
      return mapRpcRowToStatus((rows[0] ?? {}) as Record<string, unknown>);
    },
    enabled: !!propertyId,
    staleTime: CURTAILMENT_REALTIME_STALE_MS,
    refetchInterval: CURTAILMENT_REALTIME_STALE_MS,
  });

  const {
    data: dailyRows = [],
    isLoading: dailyLoading,
    error: dailyQueryError,
    refetch: refetchDaily,
  } = useQuery({
    queryKey: ['curtailment-daily', propertyId, thirtyDaysAgo],
    queryFn: async (): Promise<CurtailmentDailyRow[]> => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from('curtailment_daily')
        .select('local_date, wasted_kwh, curtailed_hours, confidence_summary')
        .eq('property_id', propertyId)
        .gte('local_date', thirtyDaysAgo)
        .order('local_date', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((r) => ({
        local_date: r.local_date,
        wasted_kwh: Number(r.wasted_kwh ?? 0),
        curtailed_hours: r.curtailed_hours ?? null,
        confidence_summary: r.confidence_summary ?? null,
      }));
    },
    enabled: !!propertyId,
    staleTime: CURTAILMENT_DAILY_STALE_MS,
  });

  const summary: CurtailmentSummary = (() => {
    const now = new Date();
    const cutoff7Str = subDays(now, 7).toISOString().slice(0, 10);
    const cutoff30Str = subDays(now, 30).toISOString().slice(0, 10);
    let wasted7dKwh = 0;
    let wasted30dKwh = 0;
    let daysWithData = 0;
    for (const row of dailyRows) {
      // Skip very_low confidence entries — unreliable estimates shouldn't inflate totals
      if (row.confidence_summary === 'very_low') continue;
      const localDate = String(row.local_date ?? '').slice(0, 10);
      if (localDate >= cutoff30Str) {
        wasted30dKwh += row.wasted_kwh;
        daysWithData += 1;
      }
      if (localDate >= cutoff7Str) wasted7dKwh += row.wasted_kwh;
    }
    return { wasted7dKwh, wasted30dKwh, daysWithData };
  })();

  return {
    status: statusRow ?? null,
    statusLoading,
    statusError: statusQueryError ? (statusQueryError as Error).message : null,
    dailyRows,
    dailyLoading,
    dailyError: dailyQueryError ? (dailyQueryError as Error).message : null,
    summary,
    refetchStatus,
    refetchDaily,
  };
}
